'use client';

import { MonitorPlay, Play } from 'lucide-react';
import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  usePlyr,
  useVideoTimestampJump,
  PlyrMarker
} from '@/utils/hooks/video';
import 'plyr/dist/plyr.css';

export interface BaseVideoPlayerProps {
  /** Video recording URL */
  recordingUrl: string | null;
  /** Thumbnail image URL (if null, video shows immediately) */
  thumbnailUrl: string | null;
  /** Whether URLs are still loading */
  loading?: boolean;
  /** Whether the video is still processing */
  isProcessing?: boolean;

  /** Duration string to display on thumbnail (e.g., "2:34") */
  duration?: string;
  /** Whether to show the duration badge on thumbnail */
  showDurationBadge?: boolean;

  /** Markers to display on the progress bar */
  markers?: PlyrMarker[];
  /** Custom Plyr controls array */
  controls?: string[];
  /** Enable speed control settings */
  enableSpeedControls?: boolean;

  /** Automatically seek to this position (in seconds) when ready */
  autoSeekTo?: number;
  /** Automatically start playback when ready */
  autoPlay?: boolean;
  /** Listen for video-jump-timestamp events */
  enableTimestampJump?: boolean;

  /** Alt text for thumbnail image */
  thumbnailAlt?: string;
  /** Whether to stop click event propagation */
  stopPropagation?: boolean;
  /** Additional CSS classes for the container */
  className?: string;
  /** Use centered video styling (for conversation videos) */
  centeredVideoStyle?: boolean;

  /** Message to display in a loading card (e.g., "Loading conversation...") */
  loadingMessage?: string;

  /** Called when Plyr is ready */
  onReady?: () => void;
}

/**
 * Base video player component with thumbnail -> video transition.
 * Pure presentational component - receives all data via props.
 *
 * Use the type-specific wrappers for most cases and create more as needed, like:
 * - ConversationVideoPlayer
 * - ClipVideoPlayer
 * - ReelVideoPlayer
 */
export function BaseVideoPlayer({
  recordingUrl,
  thumbnailUrl,
  loading = false,
  isProcessing = false,
  duration,
  showDurationBadge = false,
  markers = [],
  controls,
  enableSpeedControls = false,
  autoSeekTo,
  autoPlay = false,
  enableTimestampJump = true,
  thumbnailAlt = 'Video thumbnail',
  stopPropagation = false,
  className,
  loadingMessage,
  centeredVideoStyle = false,
  onReady
}: BaseVideoPlayerProps) {
  // Memoize controls array to prevent unnecessary Plyr re-initialization
  // when parent re-renders with a new array reference but same values
  const stableControls = useMemo(
    () => controls,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(controls)]
  );

  // State for thumbnail -> video transition
  const [showVideo, setShowVideo] = useState(autoPlay);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(autoPlay);
  const [videoReady, setVideoReady] = useState(false);

  // Start video callback
  const startVideo = useCallback(() => {
    setShouldAutoPlay(true);
    setShowVideo(true);
  }, []);

  // Initialize Plyr when video is showing
  const plyrEnabled = showVideo && !!recordingUrl;
  const { videoRef, plyrRef, isReady } = usePlyr({
    enabled: plyrEnabled,
    markers,
    controls: stableControls,
    enableSpeedControls,
    autoSeekTo,
    autoPlay: shouldAutoPlay,
    onReady: () => {
      setVideoReady(true);
      onReady?.();
    }
  });

  // Handle timestamp jump events
  useVideoTimestampJump({
    isVideoShowing: showVideo,
    isPlyrReady: isReady,
    plyrRef,
    videoRef,
    onStartVideo: enableTimestampJump ? startVideo : undefined
  });

  // If no thumbnail, show video immediately once URL is loaded
  useEffect(() => {
    if (!loading && !thumbnailUrl && recordingUrl && !showVideo) {
      setShowVideo(true);
    }
  }, [loading, thumbnailUrl, recordingUrl, showVideo]);

  const handleThumbnailClick = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    startVideo();
  };

  const handleVideoClick = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
  };

  // Loading message state
  if (loadingMessage) {
    return (
      <div className={className}>
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded bg-secondary">
            <MonitorPlay className="mb-1 size-6 animate-pulse text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {loadingMessage}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Processing state, file could not be in S3 yet
  if (isProcessing || !recordingUrl) {
    return (
      <div className={className}>
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded bg-secondary">
            <MonitorPlay className="mb-1 size-6 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Processing</span>
          </div>
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (loading) {
    return (
      <div className={className}>
        <div className="aspect-video relative animate-pulse rounded bg-secondary">
          {showDurationBadge && (
            <div className="absolute bottom-0 right-0 m-2 rounded bg-muted">
              <div className="h-6 w-16 p-2 px-3 text-sm"></div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // No thumbnail - show video directly
  if (!thumbnailUrl) {
    return (
      <div className={className}>
        {centeredVideoStyle && (
          <style>{`
            .plyr-container-centered .plyr,
            .plyr-container-centered .plyr__video-wrapper {
              height: 100% !important;
              width: 100% !important;
            }
            .plyr-container-centered video {
              height: 100% !important;
              width: 100% !important;
              object-fit: contain !important;
              object-position: center !important;
            }
          `}</style>
        )}
        <div className={centeredVideoStyle ? 'plyr-container-centered' : ''}>
          <video
            ref={videoRef}
            className="size-full"
            playsInline
            crossOrigin="anonymous"
            onClick={handleVideoClick}
          >
            <source src={recordingUrl} type="video/mp4" />
          </video>
        </div>
      </div>
    );
  }

  // With thumbnail
  return (
    <div className={className}>
      <div className="relative w-full">
        {/* Thumbnail - visible until video is ready */}
        <div
          className={`${showVideo && videoReady ? 'hidden' : ''} group relative w-full cursor-pointer bg-black`}
          onClick={handleThumbnailClick}
          style={{
            paddingBottom: '56.25%',
            ...(showVideo && !videoReady ? { pointerEvents: 'none' } : {})
          }}
        >
          <img
            src={thumbnailUrl}
            alt={thumbnailAlt}
            className="absolute inset-0 size-full object-contain"
          />
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors group-hover:bg-black/40">
            <div className="flex size-12 items-center justify-center rounded-full bg-[#00b2ff] transition-transform group-hover:scale-110">
              <Play className="ml-1 size-6 text-white" fill="currentColor" />
            </div>
          </div>

          {/* Duration badge */}
          {showDurationBadge && duration && (
            <div className="absolute bottom-0 right-0 rounded bg-black text-zinc-100">
              <div className="p-2 px-3 text-sm">{duration}</div>
            </div>
          )}

          {/* Loading overlay when video is initializing */}
          {showVideo && !videoReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="text-white">
                <svg
                  className="size-8 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
            </div>
          )}
        </div>

        {/* Video container - render when playing, show when ready */}
        {showVideo && (
          <div
            className={`overflow-hidden bg-black ${!videoReady ? 'pointer-events-none absolute inset-0 opacity-0' : 'relative w-full'}`}
            style={videoReady ? { paddingBottom: '56.25%' } : undefined}
          >
            {centeredVideoStyle && (
              <style>{`
                .plyr-container-centered .plyr,
                .plyr-container-centered .plyr__video-wrapper {
                  height: 100% !important;
                  width: 100% !important;
                }
                .plyr-container-centered video {
                  height: 100% !important;
                  width: 100% !important;
                  object-fit: contain !important;
                  object-position: center !important;
                }
              `}</style>
            )}
            <div
              className={`absolute inset-0 ${centeredVideoStyle ? 'plyr-container-centered' : ''}`}
            >
              <video
                ref={videoRef}
                className="size-full"
                playsInline
                controls
                crossOrigin="anonymous"
                onClick={handleVideoClick}
              >
                <source src={recordingUrl} type="video/mp4" />
              </video>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
